









































import Vue from 'vue';
import _ from 'lodash';
import ProductForm from '@/web/components/products/ProductForm.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import { mapGetters } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import { notify } from '@/web/utils/notify';
import { errorToMessage } from '@/core/utils/errors';
import { mapWithLimit } from '@/core/utils/concurrency';
import TopNav from '@/web/components/navigation/TopNav.vue';
import DirtyMixin from '@/web/mixins/DirtyMixin';
import { formatObjectForBackend } from '@/core/api/utils/converter';
import { getStudentViewURL } from '@/web/router/custom-domain-utils';
import ProductUpsellModal from '@/web/components/products/ProductUpsellModal.vue';

export default Vue.extend({
  name: 'EditProduct',
  mixins: [DirtyMixin],
  components: { ProductForm, NavItem, ValidationObserver, TopNav },
  computed: {
    ...mapGetters({
      productForm: 'product/productForm',
      teacherProduct: 'product/teacherProduct',
      teacherStudioUrl: 'user/teacherStudioUrl',
      teacherMagicCode: 'user/teacherMagicCode',
    }),
    isUpsellCompletelySetup() {
      if (!this.productForm) return false;
      return (
        this.productForm.upsell_product &&
        this.productForm.upsell_price &&
        this.productForm.upsell_countdown
      );
    },
  },
  data() {
    return {
      initialized: false,
      inProgress: false,
      isUpsellPreviewLoading: false,
    };
  },
  async mounted() {
    const id = parseInt(this.$route.params['id']);
    const productId = parseInt(this.teacherProduct?.id);
    if (this.teacherProduct && productId === id) {
      await this.$store.dispatch('product/setProductForm', this.teacherProduct);
      this.initialized = true;
    } else {
      const product = await this.$store.dispatch(
        'product/getTeacherProductDetail',
        id
      );
      await this.$store.dispatch('product/setProductForm', product);
      this.initialized = true;
    }

    // load for filtering content
    await this.$store.dispatch('mediaLibrary/getInstructors');
    await this.$store.dispatch('mediaLibrary/getCategories');
    await this.$store.dispatch('product/getTeacherTaxRates');
    await this.$store.dispatch('product/getProductTags');
  },
  methods: {
    async previewUpsell() {
      try {
        this.isUpsellPreviewLoading = true;
        const previewProduct = _.cloneDeep(this.productForm);
        previewProduct.upsell_seconds_remaining =
          previewProduct.upsell_countdown * 60;
        const upsoldProduct = await this.$store.dispatch(
          'product/getProductForPurchase',
          {
            studioURL: this.teacherStudioUrl,
            productId: previewProduct.upsell_product,
          }
        );
        previewProduct.upsell_product = upsoldProduct;
        previewProduct.payment_gateway = 'stripe';
        this.isUpsellPreviewLoading = false;
        this.$modal.show(ProductUpsellModal, {
          product: previewProduct,
          studioURL: this.teacherStudioUrl,
          preview: true,
        });
      } finally {
        this.isUpsellPreviewLoading = false;
      }
    },
    getStudentPreviewURL(studioUrl, productId) {
      return getStudentViewURL(
        studioUrl,
        `/product/${productId}`,
        this.teacherMagicCode
      );
    },
    navigateBack(): void {
      const id = parseInt(this.$route.params['id']);
      this.$router.push(`/products/${id}/`);
    },
    async editProduct(): Promise<void> {
      if (this.inProgress) {
        console.log('Save already in progress!');
        return;
      }
      const payload: UpdateProductRequest = formatObjectForBackend({
        ...this.productForm,
      });
      const productFormValidator = this.$refs.productFormValidator;
      const res = await productFormValidator.validate();
      if (res) {
        this.inProgress = true;
        try {
          const lessons = this.productForm.lessons.filter(
            ({ isDirty }) => isDirty
          );

          await mapWithLimit(lessons, this.updateLesson);
          await mapWithLimit(lessons, this.reorderLesson);
          await this.$store.dispatch('product/updateProduct', payload);
          const id = parseInt(this.$route.params['id']);
          await this.$store.dispatch('product/updateTeacherProductDetail', id);
          this.clearDirty();
          notify(
            this.$t('PRODUCTS.MODAL.PRODUCT_UPDATED').toString(),
            this.$t('PRODUCTS.MODAL.PRODUCT_UPDATED_DESCRIPTION').toString()
          );
        } catch (error) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Failed to save product',
            text: errorToMessage(error.response, `Request failed: ${error}`),
            duration: -1, // stay ON
          });
        }
        this.inProgress = false;
      }
    },
    updateLesson(lesson): Promise<void> {
      return this.$store.dispatch('product/updateProductLesson', {
        ...lesson,
        content: lesson.content.map(({ id }) => id),
      });
    },
    reorderLesson(lesson): Promise<void> {
      const content = lesson.content.map(({ id }, index) => ({
        media_id: id,
        position: -index,
      }));

      return this.$store.dispatch('product/reorderProductLesson', {
        productId: lesson.product,
        lessonId: lesson.id,
        content,
      });
    },
  },
  destroyed() {
    this.$store.dispatch('product/setProductForm', null);
  },
  watch: {
    productForm: {
      handler: function (productForm, oldProductForm) {
        if (oldProductForm && this.initialized) {
          this.setDirty();
        }
      },
      deep: true,
    },
  },
});
